import { useEffect, useState } from "react";
import { fetchApprovalAccess, type ApprovalAccess } from "../Layouts/work-hours/services/workHoursService";
import { useUserStore } from "../store/userStore";

// ---------------------------------------------------------------------------
// ¿Quien usa la consola aprueba horas (#1281)? Lo dice el servidor
// (`GET /workHoursApprovalAccess`): dueños, colaboradores con rol tech lead y
// líderes de squad. Sirve para PINTAR la pestaña «Horas por aprobar» de Nómina a
// quien no administra Nómina; el servidor sigue decidiendo cada petición.
// Mientras no responde (o si falla) se asume que NO aprueba.
// ---------------------------------------------------------------------------

export interface HoursApprover extends ApprovalAccess {
  /** ¿Ya respondió el servidor (o falló)? Quien redirige por no aprobar espera a esto. */
  loaded: boolean;
}

const NONE: HoursApprover = { canApprove: false, pending: 0, pendingHours: 0, loaded: false };

const cache = new Map<string, ApprovalAccess>();
const inflight = new Map<string, Promise<ApprovalAccess>>();

function load(key: string): Promise<ApprovalAccess> {
  const pending = inflight.get(key);
  if (pending) return pending;
  const p = fetchApprovalAccess()
    .then((access) => {
      cache.set(key, access);
      return access;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

/** Solo para pruebas. */
export function _resetHoursApproverCache(): void {
  cache.clear();
  inflight.clear();
}

/**
 * @param enabled `false` = no preguntar (p. ej. el tenant no tiene Horas Trabajadas).
 * @param refreshKey cambia para volver a preguntar (p. ej. tras aprobar algo).
 */
export function useHoursApprover(enabled = true, refreshKey: unknown = null): HoursApprover {
  const user = useUserStore((state) => state.user);
  const key = enabled ? String(user?.id ?? user?.email ?? "") : "";
  const [access, setAccess] = useState<HoursApprover>(() => {
    const hit = key ? cache.get(key) : undefined;
    return hit ? { ...hit, loaded: true } : NONE;
  });

  useEffect(() => {
    if (!key) {
      setAccess({ ...NONE, loaded: true });
      return;
    }
    let alive = true;
    void load(key).then((a) => {
      if (alive) setAccess({ ...a, loaded: true });
    });
    return () => {
      alive = false;
    };
  }, [key, refreshKey]);

  return access;
}
